/**
 * Context navigation: the sidebar group for wherever you have drilled into.
 *
 * Two places use it, and they are deliberately the same shape. Inside a class
 * you get that class's sections — its roster, its assignments, its grades.
 * Inside My Teaching you get the same categories, but each one gathers every
 * class you teach instead of one. So "Grades" means the same thing in both
 * places; only the scope changes.
 *
 * The sidebar sits above the router outlet, so a page publishes its group
 * through this context rather than passing it upward. Publishing null (which
 * every provider does on unmount) is what makes the group disappear on the way
 * back out.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export type SectionKey =
  | 'overview'
  | 'people'
  | 'assignments'
  | 'grades'
  | 'attendance'
  | 'standards'
  | 'schedule'
  | 'dashboard'
  | 'household'
  | 'enrollment'
  | 'invoices';

export type SectionIcon =
  | 'home' | 'users' | 'clipboard' | 'chart' | 'attendance' | 'target' | 'calendar' | 'receipt';

export interface SectionDef {
  key: SectionKey;
  label: string;
  /** Path segment under the group's basePath. Empty means the basePath itself. */
  path: string;
  icon: SectionIcon;
  /**
   * School setup rather than teaching. Teachers never see these: which blocks a
   * class meets in is a detail of how the school was configured, and surfacing
   * it in the teaching view is the SIS leaking into the LMS.
   */
  adminOnly?: boolean;
}

/**
 * Ordered the way a teacher's day runs: who is in the room, what they were
 * given, how it went, who showed up.
 */
const TEACHING_WORK: readonly SectionDef[] = [
  { key: 'people', label: 'People', path: 'people', icon: 'users' },
  { key: 'assignments', label: 'Assignments', path: 'assignments', icon: 'clipboard' },
  { key: 'grades', label: 'Grades', path: 'grades', icon: 'chart' },
  { key: 'attendance', label: 'Attendance', path: 'attendance', icon: 'attendance' },
  { key: 'standards', label: 'Standards', path: 'standards', icon: 'target' },
];

/** One class: the daily work, plus the setup only an admin should see. */
export const CLASS_SECTIONS: readonly SectionDef[] = [
  ...TEACHING_WORK,
  { key: 'schedule', label: 'Schedule', path: 'schedule', icon: 'calendar', adminOnly: true },
];

/**
 * Every class you teach, one category at a time, behind an overview.
 *
 * "My Schedule" here is the teacher's own week assembled from their classes —
 * not the class-level Schedule section, which is school setup and admin-only.
 * Same word, different question: where am I expected, versus how is this class
 * configured.
 */
export const TEACHING_SECTIONS: readonly SectionDef[] = [
  { key: 'overview', label: 'Overview', path: '', icon: 'home' },
  ...TEACHING_WORK,
  { key: 'schedule', label: 'My Schedule', path: 'schedule', icon: 'calendar' },
];

/**
 * A family's portal, in the order a parent reads it: what needs them, then the
 * record behind it. An administrator viewing as a family gets exactly this —
 * the point of the view is that it is not an admin's arrangement of the same
 * pages.
 */
export const FAMILY_SECTIONS: readonly SectionDef[] = [
  { key: 'dashboard', label: 'Dashboard', path: '', icon: 'home' },
  { key: 'household', label: 'Household', path: 'household', icon: 'users' },
  { key: 'enrollment', label: 'Enrollment', path: 'enrollment', icon: 'clipboard' },
  { key: 'invoices', label: 'Invoices', path: 'invoices', icon: 'receipt' },
];

/** The class sections a teacher may open; admins get all of them. */
export function sectionsFor(sections: readonly SectionDef[], isAdmin: boolean): SectionDef[] {
  return sections.filter((section) => isAdmin || !section.adminOnly);
}

export function sectionPath(basePath: string, section: SectionDef): string {
  return section.path ? `${basePath}/${section.path}` : basePath;
}

export interface ContextNavInfo {
  /** Distinguishes the two scopes for anything that needs to branch on it. */
  kind: 'class' | 'teaching' | 'family';
  title: string;
  subtitle?: string;
  /** Everything above the section segment. */
  basePath: string;
  sections: readonly SectionDef[];
  /**
   * Destinations that come from the data rather than from the shape of the
   * group — a family's children, each with a page. They are listed under the
   * sections because an exclusive group is the whole sidebar, and the children
   * have to be reachable from inside it.
   */
  links?: ReadonlyArray<{ id: string; name: string; path: string; heading: string }>;
  /**
   * Shown at the top of the group, for drilling back out. `go`, when given,
   * is the page's own way back (see useWayBack) and is used instead of `path`.
   */
  backLink?: { path: string; label: string; go?: () => void };
  /**
   * This group is the whole sidebar while you are here. A class is a place you
   * work inside, not a page you happen to be on, so the school's nav steps
   * aside and the back link is the way out. Scopes that sit alongside the rest
   * of the app — My Teaching — leave this off.
   */
  exclusive?: boolean;
}

interface ContextNavValue {
  contextNav: ContextNavInfo | null;
  setContextNav: (nav: ContextNavInfo | null) => void;
}

const ContextNavContext = createContext<ContextNavValue>({
  contextNav: null,
  setContextNav: () => {},
});

export function ContextNavProvider({ children }: { children: ReactNode }) {
  const [contextNav, setContextNav] = useState<ContextNavInfo | null>(null);
  const value = useMemo(() => ({ contextNav, setContextNav }), [contextNav]);
  return <ContextNavContext.Provider value={value}>{children}</ContextNavContext.Provider>;
}

export function useContextNav(): ContextNavValue {
  return useContext(ContextNavContext);
}
